import type { SerializedStyles } from '@emotion/react'
import type { ReactNode } from 'react'
import { UnifiedDropdown } from '../UnifiedDropdown'
import type { UnifiedDropdownProps, OnChangeEventContext } from '../UnifiedDropdown'

export type { OnChangeEventContext }

export interface OnInputChangeEventContext {
  name?: string
  value: string
}

export interface AllOption {
  value: string
  id: string
}

export interface GetOptionLabelParameters {
  option?: object
  labelKey?: string
  selected?: boolean
}

export interface OptionProps {
  getOptionLabel: (parameters: GetOptionLabelParameters) => string
  labelKey?: string
  option: object
  selected?: boolean
  allOption?: AllOption
  allowSelectAll?: boolean
  hideCheckboxes?: boolean
  isFocused?: boolean
  onOptionClick?: (event: React.SyntheticEvent<any>) => void
  onOptionFocus?: (event: React.SyntheticEvent<any>) => void
  optionDisabledKey?: string
  optionRef?: (element: HTMLElement) => void
  optionRenderer?: ReactNode
  staticOption?: object | object[]
  valueKey?: string
}

export enum DropdownMode {
  Filter = 'filter',
  Multi = 'multi',
  Simple = 'simple'
}

export enum StyleVariant {
  Outline = 'outline',
  Filled = 'filled',
  Transparent = 'transparent'
}

export interface BaseDropdownProps {
  /** Accessible label for the dropdown combobox */
  ariaLabel?: string | null
  /** Custom CSS styles */
  css?: SerializedStyles | SerializedStyles[]
  /** Indicates field is an error state */
  error?: boolean
  /** Label for the dropdown */
  label?: string
  /** Type of dropdown to use */
  mode?: DropdownMode
  /** Indicates that label should be screen-ready only. */
  srOnlyLabel?: boolean
  /** Style variant of dropdown. */
  styleVariant?: StyleVariant
  /** Custom aria-label for the dropdown menu */
  menuAriaLabel?: string
  /** Element to render before the input */
  beforeInput?: ReactNode
  /** Whether to clear input on blur */
  clearInputOnBlur?: boolean
  /** Whether to clear input on select */
  clearInputOnSelect?: boolean
  /** Custom delimiter for multi-select values */
  delimiter?: string
  /** Whether dropdown is disabled */
  disabled?: boolean
  /** Custom function to get option label */
  getOptionLabel?: (params: { option: any }) => string
  /** ID for the input element */
  inputId?: string
  /** Controlled input value */
  inputValue?: string
  /** Whether the dropdown is searchable */
  isSearchable?: boolean
  /** Text to display when no results found */
  noResultsText?: string
  /** Placeholder text */
  placeholder?: any
  /** Blur handler */
  onBlur?: (e: any) => void
  /** Change handler */
  onChange?: (e: any) => void
  /** Focus handler */
  onFocus?: (e: any) => void
  /** Input change handler */
  onInputChange?: (e: any) => void
  /** Key down handler */
  onKeyDown?: (e: any) => void
  /** Select handler for multi-select */
  onSelect?: (e: any) => void
  /** Static option click handler */
  onStaticOptionClick?: (...args: any[]) => void
  /** Callback to clear the selected value. When provided, an X button appears inside the dropdown when a value is selected. */
  onClear?: () => void
  /** Whether to open dropdown on empty input */
  openOnEmptyInput?: boolean
  /** Custom option renderer */
  optionRenderer?: (option: any) => ReactNode
  /** Whether to show the input field */
  showInput?: boolean
  /** Whether to show values while focused (multi-select) */
  showValuesWhileFocused?: boolean
  /** Static option to show (e.g., "Add new" option) */
  staticOption?: any
  /** Custom value component renderer */
  valueComponent?: any
  /** Custom value group renderer */
  valueGroupRenderer?: ((props: { option: any }) => ReactNode) | any
  /** Limit for number of value labels to show */
  valueLabelLimit?: number
  /** Custom value renderer */
  valueRenderer?: (props: { option: any }) => ReactNode
}

export interface DropdownProps
  extends BaseDropdownProps,
    Omit<
      UnifiedDropdownProps,
      | 'isMulti'
      | 'isFilterable'
      | 'label'
      | 'onChange'
      | 'placeholder'
      | 'styleVariant'
      | 'onInputChange'
      | 'inputValue'
      | 'beforeInput'
      | 'valueComponent'
      | 'valueRenderer'
      | 'onSelect'
      | 'onStaticOptionClick'
      | 'getOptionLabel'
      | 'onBlur'
      | 'onFocus'
      | 'onKeyDown'
      | 'clearInputOnBlur'
      | 'delimiter'
      | 'inputId'
      | 'menuAriaLabel'
      | 'optionRenderer'
      | 'showInput'
      | 'staticOption'
    > {}

export interface MultiDropdownProps
  extends BaseDropdownProps,
    Omit<
      UnifiedDropdownProps,
      | 'isMulti'
      | 'isFilterable'
      | 'label'
      | 'onChange'
      | 'placeholder'
      | 'styleVariant'
      | 'onInputChange'
      | 'inputValue'
      | 'beforeInput'
      | 'valueComponent'
      | 'valueRenderer'
      | 'onSelect'
      | 'onStaticOptionClick'
      | 'getOptionLabel'
      | 'onBlur'
      | 'onFocus'
      | 'onKeyDown'
      | 'clearInputOnBlur'
      | 'delimiter'
      | 'inputId'
      | 'menuAriaLabel'
      | 'optionRenderer'
      | 'showInput'
      | 'staticOption'
    > {}

export interface FilterDropdownProps
  extends BaseDropdownProps,
    Omit<
      UnifiedDropdownProps,
      | 'isMulti'
      | 'isFilterable'
      | 'label'
      | 'onChange'
      | 'placeholder'
      | 'styleVariant'
      | 'onInputChange'
      | 'inputValue'
      | 'beforeInput'
      | 'valueComponent'
      | 'valueRenderer'
      | 'onSelect'
      | 'onStaticOptionClick'
      | 'getOptionLabel'
      | 'onBlur'
      | 'onFocus'
      | 'onKeyDown'
      | 'clearInputOnBlur'
      | 'delimiter'
      | 'inputId'
      | 'menuAriaLabel'
      | 'optionRenderer'
      | 'showInput'
      | 'staticOption'
    > {}

type AllProps = DropdownProps | MultiDropdownProps | FilterDropdownProps

/* eslint-disable @typescript-eslint/no-unused-vars -- destructured props are swallowed (not forwarded via ...props) intentionally */
const Dropdown = ({
  ariaLabel,
  css: customCss,
  error = false,
  label,
  mode = DropdownMode.Simple,
  srOnlyLabel,
  styleVariant = StyleVariant.Outline,
  menuAriaLabel,
  beforeInput,
  clearInputOnBlur,
  clearInputOnSelect,
  delimiter,
  disabled,
  getOptionLabel,
  inputId,
  inputValue,
  isSearchable,
  noResultsText,
  onBlur,
  onChange,
  onClear,
  onFocus,
  onInputChange,
  onKeyDown,
  onSelect,
  onStaticOptionClick,
  openOnEmptyInput,
  optionRenderer,
  placeholder = '',
  showInput,
  showValuesWhileFocused,
  staticOption,
  valueComponent,
  valueGroupRenderer,
  valueLabelLimit,
  valueRenderer,
  ...props
}: AllProps) => {
  /* eslint-enable @typescript-eslint/no-unused-vars */
  // Determine UnifiedDropdown mode based on DropdownMode
  const isMulti = mode === DropdownMode.Multi
  const isFilterable = mode === DropdownMode.Filter || isSearchable === true

  const handleChange = onChange ? (e: OnChangeEventContext) => onChange(e) : null

  return (
    <div css={customCss}>
      <UnifiedDropdown
        {...props}
        ariaLabel={ariaLabel}
        beforeInput={beforeInput}
        clearInputOnBlur={clearInputOnBlur}
        delimiter={delimiter}
        getOptionLabel={getOptionLabel}
        inputId={inputId}
        isMulti={isMulti}
        isFilterable={isFilterable}
        label={label}
        menuAriaLabel={menuAriaLabel}
        onBlur={onBlur}
        onChange={handleChange}
        onClear={onClear}
        onFocus={onFocus}
        onKeyDown={onKeyDown}
        onSelect={onSelect}
        onStaticOptionClick={onStaticOptionClick}
        optionRenderer={optionRenderer}
        placeholder={placeholder}
        showInput={showInput}
        staticOption={staticOption}
        disabled={disabled}
        styleVariant={styleVariant}
        inputValue={inputValue}
        onInputChange={onInputChange}
        valueComponent={valueComponent}
        valueRenderer={valueRenderer || valueGroupRenderer}
      />
    </div>
  )
}

export default Dropdown
